import { generateQueryField, trackEvent } from '@patrianna/analytics'
import { cleanDialogStack, closeDialogByName, closeLatestDialog, openDialog } from '@patrianna/dialog-manager'
import {
  authDetectExpiredSession,
  authLogin,
  authLogoutRequest,
  authRegisterViaEmail,
  authResendEmailRequest,
  authRunsCurrentAuthFlow,
  authSignInPermanent,
  authSocialSignInHandler,
  authSocialSignUpHandler,
  resetPasswordAction,
  setAuthLoading,
} from '@patrianna/shared-store/auth'
import { setCookie } from '@patrianna/shared-utils/cookie'
import { clearPaymentsState } from '@patrianna-payments/shared-store/payments/store/actions'
import { clearRedeemState } from '@patrianna-payments/shared-store/redeem'

import { getSeonSessionId } from 'services/seon'
import { getFacebookUserData, initFacebookApp } from 'src/analyticActions/facebook/utils'
import { FACEBOOK_APP_ID } from 'src/config/apps'
import ROUTES from 'src/config/routes.json'
import { THIRTY_MINUTES } from 'src/containers/VerifyEmail/useResendEmail'
import { getLocationHref, getLocationPathname, navigateTo } from 'src/utils/browserLocation'
import { clearAuthCookies } from 'src/utils/clearAuthCookies'
import { getForcementModeSelector } from 'store/modules/appConfig/selectors'
import { clearCurrency } from 'store/modules/currencies/actions'
import { clearPlayedGames } from 'store/modules/playedGames/actions'
import { openSnackbar } from 'store/modules/snackbar/actions'
import {
  deleteUser,
  getSignUpFraudInfo,
  setSignUpFraudInfo,
  LOYALTY_LAST_ACTIVITY_KEY,
  LOYALTY_INACTIVITY_SHOWN_KEY,
  resetLastActivitySession,
} from 'store/modules/user/actions'
import { getUserPasswordChanged, isLoggedInSelector, isSignUpSessionSelector } from 'store/modules/user/selectors'
import {
  IS_SUCCESS_PASSWORD_CHANGE,
  IS_USER_REGISTERED,
  LS_AUTH_TYPE,
  SAVED_UPCOMMING_GAMES,
  getItemFormLocalStorageSelector,
  removeDataFromLocalStorage,
  setDataToLocalStorage,
} from 'utils/localStorage'

import type { AuthErrorHandleActionName } from '@patrianna/shared-store/auth'
import type { TypedThunkAuth } from '@patrianna/shared-store/auth/store'
import type { useSearchParams } from 'next/navigation'
import type { TypedThunk } from 'src/store/types'
import type { AuthMethod, AuthViaSocialAPI, RegisterViaEmail } from 'store/modules/auth/types'

const AUTO_AUTH_LOG_PREFIX = '[AUTO_AUTH_DEBUG]'

const getAutoAuthQueryMeta = (routerQuery: ReturnType<typeof useSearchParams>, routerPath: string) => {
  const a = routerQuery.get('a')

  return {
    routerPath,
    hasAParam: Boolean(a),
    aParamLength: a?.length ?? 0,
    aParamPreview: a ? `${a.slice(0, 8)}...` : '',
    gameDialogName: routerQuery.get('gameDialogName') || '',
    gameCode: routerQuery.get('gameCode') || '',
    invitedBy: routerQuery.get('invited_by') || '',
  }
}

const tokenMeta = (token: string) => ({
  tokenLength: token?.length ?? 0,
  tokenPreview: token ? `${token.slice(0, 8)}...` : '',
})

export { resetPasswordAction, setAuthLoading }

export const authSuccessHandle = (): TypedThunk => (dispatch, getState) => {
  console.info(`${AUTO_AUTH_LOG_PREFIX} authSuccessHandle`, {
    href: getLocationHref(),
    pathname: getLocationPathname(),
  })

  closeDialogByName({ dialogName: 'AUTH_CONFIRM_DIALOG' })
  closeDialogByName({ dialogName: 'PRELOADER_DIALOG' })
  closeDialogByName({ dialogName: 'OTP_HELLO_AGAIN_DIALOG' })
  closeDialogByName({ dialogName: 'USER_EXISTS_DIALOG' })
  closeDialogByName({ dialogName: 'CONNECT_ACCOUNT_DIALOG' }) // not used

  dispatch(setDataToLocalStorage(IS_USER_REGISTERED, true, false))

  dispatch(removeDataFromLocalStorage(SAVED_UPCOMMING_GAMES))
  dispatch(removeDataFromLocalStorage('REFERRER', false))
  dispatch(removeDataFromLocalStorage('QUERY', false))

  if (!getLocationHref().includes(ROUTES.CALLBACK)) {
    dispatch(setAuthLoading({ loading: false }))
  }

  setCookie('LOGGED_IN', '1')
  setCookie('USER_REGISTERED', '1')

  const isSignUpSession = isSignUpSessionSelector(getState())

  if (isSignUpSession) {
    setTimeout(() => {
      dispatch(getSignUpFraudInfo())
    }, 5000)
  } else {
    dispatch(setSignUpFraudInfo({ fraudCheckCompleted: true, geoVerificationRequired: false }))
  }
}

export const authErrorHandler =
  ({ err_code, oauth, options, routerPush, routerPath }: AuthErrorHandleActionName): TypedThunk =>
  (dispatch, getState) => {
    const isCallbackPage = getLocationHref().includes(ROUTES.CALLBACK)
    console.warn(`${AUTO_AUTH_LOG_PREFIX} authErrorHandler`, {
      errCode: err_code,
      oauth: oauth || '',
      routerPath,
      isCallbackPage,
      hasErrorText: Boolean(options?.errorText),
    })

    // get data for event tracking
    const authTypeFromLocalStorage = getItemFormLocalStorageSelector(getState(), LS_AUTH_TYPE, false)
    const isRegisterAuth = authTypeFromLocalStorage === 'REGISTRATION'
    const authType = authTypeFromLocalStorage
      ? isRegisterAuth
        ? 'register'
        : authTypeFromLocalStorage.toLowerCase()
      : ''
    const isAuthPage = routerPath === ROUTES.REGISTER || routerPath === ROUTES.LOGIN
    const eventPrefix = isAuthPage ? routerPath.slice(1) : authType
    const userExistsEventName = eventPrefix.length ? `${eventPrefix}_hello_again_popup` : ''

    if ('isMobile' in options && !options.isMobile) {
      closeLatestDialog()
    }

    dispatch(setAuthLoading({ loading: false }))

    switch (err_code) {
      case 'err_not_found': {
        console.warn(`${AUTO_AUTH_LOG_PREFIX} authErrorHandler:redirect`, {
          reason: 'err_not_found',
          target: ROUTES.REGISTER,
        })
        routerPush(ROUTES.REGISTER)
        break
      }
      case 'err_duplicate': {
        cleanDialogStack()

        if (oauth) {
          if (isCallbackPage) {
            if (userExistsEventName.length) {
              trackEvent(userExistsEventName)
            }

            routerPush(routerPath, () => {
              openDialog({
                dialogName: 'USER_EXISTS_DIALOG',
                dialogProps: {
                  provider: oauth,
                  routerPath: routerPath,
                },
              })
            })
          } else {
            if (userExistsEventName.length) {
              trackEvent(userExistsEventName)
            }

            openDialog({
              dialogName: 'USER_EXISTS_DIALOG',
              dialogProps: {
                provider: oauth,
                routerPath: routerPath,
              },
            })
          }
        }

        if (options?.errorText && !oauth) {
          if (isCallbackPage) {
            routerPush(routerPath, () => {
              setTimeout(() => {
                dispatch(
                  openSnackbar({
                    message: options?.errorText,
                  }),
                )
              }, 300)
            })
          } else {
            dispatch(
              openSnackbar({
                message: options?.errorText,
              }),
            )
          }
        }

        break
      }

      case 'err_denied': {
        if (!options?.errorText) {
          break
        }

        if (isCallbackPage) {
          routerPush(routerPath, () => {
            setTimeout(() => {
              dispatch(
                openSnackbar({
                  message: options.errorText,
                }),
              )
            }, 300)
          })
        } else {
          dispatch(
            openSnackbar({
              message: options.errorText,
            }),
          )
        }

        break
      }

      default: {
        if (isCallbackPage) {
          routerPush(routerPath, () => {
            setTimeout(() => {
              dispatch(
                openSnackbar({
                  message: options?.errorText || 'Please, try again later',
                }),
              )
            }, 300)
          })
        } else {
          dispatch(
            openSnackbar({
              message: options?.errorText || 'Please, try again later',
            }),
          )
        }

        break
      }
    }
  }

export const login: (
  args: Omit<Parameters<typeof authLogin>[0], 'query' | 'session' | 'brandName'>,
) => TypedThunk & TypedThunkAuth = (args) => async (dispatch, getState) => {
  const query = getItemFormLocalStorageSelector(getState(), 'QUERY', false)
  const session = await getSeonSessionId()

  dispatch(
    authLogin({
      ...args,
      query,
      session,
      brandName: process.env.BRAND_NAME,
    }),
  )
}

export const socialSignInHandler =
  (
    form: AuthViaSocialAPI,
    method: AuthMethod,
    isMobile: boolean,
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
    email?: string,
  ): TypedThunk & TypedThunkAuth =>
  async (dispatch, getState) => {
    const forcement = getForcementModeSelector(getState())
    const query = getItemFormLocalStorageSelector(getState(), 'QUERY', false)
    const session = await getSeonSessionId()
    const authType = getItemFormLocalStorageSelector(getState(), LS_AUTH_TYPE, false)

    dispatch(setAuthLoading({ loading: true }))
    dispatch(
      authSocialSignInHandler({
        form,
        method,
        query,
        email,
        brandName: process.env.BRAND_NAME,
        session,
        forcement,
        isMobile,
        routerPush,
        routerQuery,
        routerPath,
        showSuccessDialog: () => {
          let loggedInDialogShown = false

          if (authType === 'REGISTRATION') {
            routerPush(routerPath, () => {
              if (!loggedInDialogShown) {
                openDialog({ dialogName: 'USER_BEEN_LOGGED_IN_DIALOG' })
              }
              trackEvent('register_logged_in_popup')
              dispatch(setAuthLoading({ loading: false }))
            })

            if (getLocationPathname() === ROUTES.HOME) {
              loggedInDialogShown = true
              openDialog({ dialogName: 'USER_BEEN_LOGGED_IN_DIALOG' })
            }
          } else {
            dispatch(setAuthLoading({ loading: false }))
          }
        },
      }),
    )
  }

export const signInPermanent =
  (
    token: string,
    launchGameParams: { gameDialogName: string; gameCode: string },
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
  ): TypedThunkAuth =>
  async (dispatch) => {
    const queryMeta = getAutoAuthQueryMeta(routerQuery, routerPath)
    console.info(`${AUTO_AUTH_LOG_PREFIX} signInPermanent:start`, {
      ...tokenMeta(token),
      ...queryMeta,
      hasLaunchGameParams: Boolean(launchGameParams?.gameDialogName || launchGameParams?.gameCode),
      launchGameParams,
    })

    try {
      const session = await getSeonSessionId()
      console.info(`${AUTO_AUTH_LOG_PREFIX} signInPermanent:sessionReceived`, {
        ...queryMeta,
        hasSession: Boolean(session),
      })

      dispatch(
        authSignInPermanent({
          token,
          launchGameParams,
          session,
          routerPush,
          routerQuery,
          routerPath,
          route: ROUTES.HOME,
        }),
      )

      console.info(`${AUTO_AUTH_LOG_PREFIX} signInPermanent:dispatched`, {
        ...queryMeta,
        route: ROUTES.HOME,
      })
    } catch (err: any) {
      console.error(`${AUTO_AUTH_LOG_PREFIX} signInPermanent:error`, {
        ...queryMeta,
        errorMessage: err?.message || '',
        errorName: err?.name || '',
      })
      throw err
    }
  }

export const registerViaEmail =
  (
    form: RegisterViaEmail,
    setSubmitting: (arg: boolean) => void,
    isMobile: boolean,
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
  ): TypedThunk & TypedThunkAuth =>
  async (dispatch, getState) => {
    const forcement = getForcementModeSelector(getState())
    const referrer = getItemFormLocalStorageSelector(getState(), 'REFERRER', false)
    const query = getItemFormLocalStorageSelector(getState(), 'QUERY', false)
    const first_utm = getItemFormLocalStorageSelector(getState(), 'FIRST_UTM', false)
    const session = await getSeonSessionId()
    const expiration = new Date().getTime() + THIRTY_MINUTES

    dispatch(
      authRegisterViaEmail({
        form,
        setSubmitting,
        isMobile,
        forcement,
        referrer,
        session,
        query: generateQueryField(first_utm, query),
        first_utm,
        brandName: process.env.BRAND_NAME,
        routerPush,
        routerQuery,
        routerPath,
        route: ROUTES.CONFIRM_EMAIL,
        doNotSendSMS: true,
      }),
    )
    dispatch(setDataToLocalStorage('TIME_OF_LINK_EXPIRATION', expiration.toString(), false))
  }

export const socialSignUpHandler =
  (
    form: AuthViaSocialAPI,
    method: AuthMethod,
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
    setSubmitting?: (arg: boolean) => void,
    isMobile?: boolean,
    locale?: string,
    isFacebookLinking?: boolean,
  ): TypedThunk & TypedThunkAuth =>
  async (dispatch, getState) => {
    const forcement = getForcementModeSelector(getState())
    const referrer = getItemFormLocalStorageSelector(getState(), 'REFERRER', false)
    const query = getItemFormLocalStorageSelector(getState(), 'QUERY', false)
    const first_utm = getItemFormLocalStorageSelector(getState(), 'FIRST_UTM', false)
    const session = await getSeonSessionId()

    dispatch(
      authSocialSignUpHandler({
        form,
        method,
        setSubmitting,
        isMobile,
        forcement,
        referrer,
        session,
        query: generateQueryField(first_utm, query),
        first_utm,
        brandName: process.env.BRAND_NAME,
        routerPush,
        routerQuery,
        routerPath,
        route: ROUTES.HOME,
        doNotSendSMS: true,
        locale,
        isFacebookLinking,
      }),
    )
  }

export const logOutSuccess =
  ({ cbUrl }: { cbUrl: string }): TypedThunk =>
  (dispatch, getState) => {
    const isUserPasswordChanged = getUserPasswordChanged(getState())

    trackEvent('set_user_id', {})
    dispatch(removeDataFromLocalStorage(SAVED_UPCOMMING_GAMES))
    localStorage.removeItem(LOYALTY_LAST_ACTIVITY_KEY)
    sessionStorage.removeItem(LOYALTY_INACTIVITY_SHOWN_KEY)
    resetLastActivitySession()

    dispatch(deleteUser())
    dispatch(clearCurrency())
    dispatch(clearPlayedGames())
    dispatch(clearRedeemState())
    dispatch(clearPaymentsState())

    clearAuthCookies()

    if (isUserPasswordChanged) {
      navigateTo(ROUTES.CHANGE_PASSWORD)
      dispatch(setDataToLocalStorage(IS_SUCCESS_PASSWORD_CHANGE, true, false))
    } else {
      navigateTo(cbUrl)
    }
  }

export const logoutRequest =
  (cbUrl: string = ROUTES.HOME): TypedThunk & TypedThunkAuth =>
  (dispatch) => {
    dispatch(authLogoutRequest({ cbUrl, brandName: process.env.BRAND_NAME }))
  }

export const logOut = (): TypedThunk => (dispatch, getState) => {
  const loggedIn = isLoggedInSelector(getState())

  if (loggedIn) {
    dispatch(logoutRequest())
  } else {
    console.log("you've already logged out")
  }
}

export const socialAuthRedirectHanlder =
  (
    token: string,
    email: string,
    socialProvider: 'Facebook' | 'Google',
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
  ): TypedThunk & TypedThunkAuth =>
  async (dispatch) => {
    console.info(`${AUTO_AUTH_LOG_PREFIX} socialAuthRedirectHandler:start`, {
      ...tokenMeta(token),
      socialProvider,
      hasEmail: Boolean(email),
      ...getAutoAuthQueryMeta(routerQuery, routerPath),
    })
    dispatch(
      socialSignInHandler({ accessToken: token }, socialProvider, false, routerPush, routerQuery, routerPath, email),
    )
    console.info(`${AUTO_AUTH_LOG_PREFIX} socialAuthRedirectHandler:dispatched`, {
      socialProvider,
      ...getAutoAuthQueryMeta(routerQuery, routerPath),
    })
  }

export const runsCurrentAuthFlow =
  (
    isLoggedIn: boolean,
    routerPush: (path: string, callback?: () => void) => void,
    routerQuery: ReturnType<typeof useSearchParams>,
    routerPath: string,
  ): TypedThunkAuth =>
  async (dispatch) => {
    const query = {
      a: routerQuery.get('a'),
      gameDialogName: routerQuery.get('gameDialogName'),
      gameCode: routerQuery.get('gameCode'),
      invited_by: routerQuery.get('invited_by'),
    }
    const queryMeta = getAutoAuthQueryMeta(routerQuery, routerPath)

    console.info(`${AUTO_AUTH_LOG_PREFIX} runsCurrentAuthFlow:start`, {
      ...queryMeta,
      isLoggedIn,
      href: getLocationHref(),
    })

    await initFacebookApp(FACEBOOK_APP_ID).catch((err) => {
      console.info('Facebook SDK loadScript error', err)
      console.warn(`${AUTO_AUTH_LOG_PREFIX} runsCurrentAuthFlow:facebookInitFailed`, {
        ...queryMeta,
        errorMessage: err?.message || '',
      })
    })

    console.info(`${AUTO_AUTH_LOG_PREFIX} runsCurrentAuthFlow:dispatchSharedAction`, {
      ...queryMeta,
      isLoggedIn,
      route: ROUTES.REGISTER,
    })
    dispatch(
      authRunsCurrentAuthFlow({
        isLoggedIn,
        query,
        signInPermanent,
        socialAuthRedirectHanlder,
        getFacebookUserData,
        routerPush,
        routerQuery,
        routerPath,
        route: ROUTES.REGISTER,
      }),
    )
    console.info(`${AUTO_AUTH_LOG_PREFIX} runsCurrentAuthFlow:dispatched`, {
      ...queryMeta,
      isLoggedIn,
    })
  }

export const resendEmailRequest =
  (): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    // @ts-ignore
    dispatch(authResendEmailRequest({ gatewayInstance: gateway, gatewayErrorInstance: { errorHandler } }))
  }

export const detectExpiredSession =
  (isServerLoggedIn: boolean): TypedThunk & TypedThunkAuth =>
  (dispatch, getState) => {
    const isClientLoggedIn = isLoggedInSelector(getState())

    dispatch(authDetectExpiredSession({ isServerLoggedIn, isClientLoggedIn, cbUrl: ROUTES.HOME }))
  }
